import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Copy, X, Info } from 'lucide-react';

// Screen name mapping for easy identification
const SCREEN_NAMES: Record<string, string> = {
  '/': 'SCREEN_HOME_DASHBOARD',
  '/login': 'SCREEN_LOGIN',
  '/register': 'SCREEN_REGISTER',
  '/new-register': 'SCREEN_NEW_REGISTRATION_FLOW',
  '/register-flow': 'SCREEN_REGISTRATION_FLOW',
  '/verification': 'SCREEN_PHONE_VERIFICATION',
  '/admin-pending': 'SCREEN_ADMIN_PENDING',
  '/pending-verification': 'SCREEN_PENDING_VERIFICATION',
  '/profile-completion': 'SCREEN_PROFILE_COMPLETION',
  '/complete-profile': 'SCREEN_PROFILE_COMPLETION',
  '/mobile': 'SCREEN_MOBILE_APP',
  '/admin': 'SCREEN_ADMIN_DASHBOARD',
  '/marketing-manager': 'SCREEN_MARKETING_MANAGER',
  '/blogs': 'SCREEN_BLOGS',
  '/community': 'SCREEN_COMMUNITY_PUBLIC',
  '/community-logged': 'SCREEN_COMMUNITY_LOGGED_IN',
  '/refer-earn': 'SCREEN_REFER_EARN_PUBLIC',
  '/refer-earn-logged': 'SCREEN_REFER_EARN_LOGGED_IN',
  '/search': 'SCREEN_ENHANCED_SEARCH',
  '/advanced-matching': 'SCREEN_ADVANCED_MATCHING',
  '/help': 'SCREEN_HELP_SUPPORT',
  '/contact': 'SCREEN_CONTACT_US',
  '/contact-us': 'SCREEN_CONTACT_US',
  '/change-password': 'SCREEN_CHANGE_PASSWORD',
  '/reset-password': 'SCREEN_RESET_PASSWORD',
  '/edit-profile': 'SCREEN_EDIT_PROFILE',
  '/premium-marketplace': 'SCREEN_PREMIUM_MARKETPLACE',
  '/terms': 'SCREEN_TERMS_CONDITIONS',
  '/privacy': 'SCREEN_PRIVACY_POLICY',
  '/premium-shop': 'SCREEN_PREMIUM_SHOP',
  '/marketing-panel': 'SCREEN_MARKETING_PANEL',
  '/kundli': 'SCREEN_KUNDLI_MAKER',
  '/kundli-maker': 'SCREEN_KUNDLI_MAKER',
  '/app': 'SCREEN_MATCHES_BROWSE',
  '/app/matches': 'SCREEN_MATCHES_BROWSE',
  '/app/interests': 'SCREEN_INTERESTS_CONNECTIONS',
  '/app/chat': 'SCREEN_CHAT_LIST',
  '/app/settings': 'SCREEN_SETTINGS',
  '/matches': 'SCREEN_MATCHES_BROWSE',
  '/interests': 'SCREEN_INTERESTS_CONNECTIONS',
  '/settings': 'SCREEN_SETTINGS',
  '/notifications': 'SCREEN_NOTIFICATIONS',
  '/profile-visitors': 'SCREEN_PROFILE_VISITORS',
  '/shortlisted': 'SCREEN_SHORTLISTED_PROFILES',
  '/blocked': 'SCREEN_BLOCKED_PROFILES',
  '/success-stories': 'SCREEN_SUCCESS_STORIES',
  '/astrology': 'SCREEN_ASTROLOGY_COMPATIBILITY',
  '/photo-privacy': 'SCREEN_PHOTO_PRIVACY_SETTINGS',
  '/account-settings': 'SCREEN_ACCOUNT_SETTINGS',
  '/payment-history': 'SCREEN_PAYMENT_HISTORY',
  '/delete-account': 'SCREEN_DELETE_ACCOUNT',
};

// Dynamic route patterns
const getDynamicScreenName = (pathname: string): string => {
  if (pathname.startsWith('/profile/')) {
    return 'SCREEN_VIEW_PROFILE';
  }
  if (pathname.startsWith('/chat/')) {
    return 'SCREEN_CHAT_CONVERSATION';
  }
  if (pathname.startsWith('/blog/')) {
    return 'SCREEN_BLOG_DETAIL';
  }
  if (pathname.startsWith('/user/')) {
    return 'SCREEN_USER_PROFILE';
  }
  return 'SCREEN_UNKNOWN';
};

const DebugScreenInfo: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);

  useEffect(() => {
    // Check if debug mode is enabled
    const debugMode = import.meta.env?.DEV || 
                      import.meta.env?.VITE_DEBUG_MODE === 'true' ||
                      localStorage.getItem('figma_debug_mode') === 'true';
    setIsDebugMode(debugMode);
  }, []);

  const currentScreenName = SCREEN_NAMES[location.pathname] || getDynamicScreenName(location.pathname);
  const currentPath = location.pathname;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isDebugMode) return null;

  return (
    <>
      {/* Floating Debug Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-[9999] bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
        title="Debug Screen Info"
      >
        <Info className="w-6 h-6" />
      </button>

      {/* Debug Info Modal */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-[9999] bg-white rounded-lg shadow-2xl border-2 border-purple-600 w-96 max-w-[calc(100vw-3rem)]">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-3 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5" />
              <h3 className="font-semibold">Debug Screen Info</h3>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 rounded p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 space-y-4">
            {/* Screen Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Screen Name (For Updates)
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 px-3 py-2 rounded border border-gray-300 text-sm font-mono text-purple-700 break-all">
                  {currentScreenName}
                </code>
                <button
                  onClick={() => copyToClipboard(currentScreenName)}
                  className="p-2 bg-purple-100 hover:bg-purple-200 rounded transition-colors"
                  title="Copy screen name"
                >
                  <Copy className="w-4 h-4 text-purple-600" />
                </button>
              </div>
              {copied && (
                <p className="text-xs text-green-600 mt-1">✓ Copied to clipboard!</p>
              )}
            </div>

            {/* Current Path */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Route
              </label>
              <code className="block bg-gray-100 px-3 py-2 rounded border border-gray-300 text-sm font-mono text-gray-700 break-all">
                {currentPath}
              </code>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                📝 Update Instructions
              </h4>
              <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                <li>Copy the screen name above</li>
                <li>Tell AI: "Update <strong>{currentScreenName}</strong>"</li>
                <li>Provide your HTML code for this screen</li>
                <li>AI will update only this specific screen</li>
              </ol>
            </div>

            {/* Component File Hint */}
            <div className="bg-amber-50 border border-amber-200 rounded p-3">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">
                📂 Likely Component File
              </h4>
              <code className="text-xs text-amber-800 break-all">
                {getComponentFileName(currentScreenName)}
              </code>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Helper function to guess component file name
const getComponentFileName = (screenName: string): string => {
  const fileMap: Record<string, string> = {
    'SCREEN_HOME_DASHBOARD': '/components/HomePage.tsx',
    'SCREEN_LOGIN': '/components/LoginPage.tsx',
    'SCREEN_REGISTER': '/components/RegisterPage.tsx',
    'SCREEN_NEW_REGISTRATION_FLOW': '/components/NewRegistrationFlow.tsx',
    'SCREEN_REGISTRATION_FLOW': '/components/RegistrationFlow.tsx',
    'SCREEN_PHONE_VERIFICATION': '/components/VerificationScreen.tsx',
    'SCREEN_ADMIN_PENDING': '/components/AdminPendingScreen.tsx',
    'SCREEN_PENDING_VERIFICATION': '/components/PendingVerificationScreen.tsx',
    'SCREEN_PROFILE_COMPLETION': '/components/ProfileCompletionForm.tsx',
    'SCREEN_MOBILE_APP': '/components/MobileApp.tsx',
    'SCREEN_ADMIN_DASHBOARD': '/components/AdminDashboardReal.tsx',
    'SCREEN_MARKETING_MANAGER': '/components/marketing-manager/MarketingManagerDashboard.tsx',
    'SCREEN_BLOGS': '/components/BlogsPage.tsx',
    'SCREEN_COMMUNITY_PUBLIC': '/components/CommunityPage.tsx',
    'SCREEN_COMMUNITY_LOGGED_IN': '/components/CommunityLoggedIn.tsx',
    'SCREEN_REFER_EARN_PUBLIC': '/components/ReferEarnPage.tsx',
    'SCREEN_REFER_EARN_LOGGED_IN': '/components/ReferEarnLoggedIn.tsx',
    'SCREEN_ENHANCED_SEARCH': '/components/EnhancedSearchPage.tsx',
    'SCREEN_ADVANCED_MATCHING': '/components/AdvancedMatchingPage.tsx',
    'SCREEN_HELP_SUPPORT': '/components/HelpSupportPage.tsx',
    'SCREEN_CONTACT_US': '/components/ContactUsPage.tsx',
    'SCREEN_CHANGE_PASSWORD': '/components/ChangePasswordPage.tsx',
    'SCREEN_RESET_PASSWORD': '/components/ResetPasswordPage.tsx',
    'SCREEN_EDIT_PROFILE': '/components/EditProfilePageProduction.tsx',
    'SCREEN_PREMIUM_MARKETPLACE': '/components/PremiumMarketplace.tsx',
    'SCREEN_TERMS_CONDITIONS': '/components/TermsConditionsPage.tsx',
    'SCREEN_PRIVACY_POLICY': '/components/PrivacyPolicyPage.tsx',
    'SCREEN_PREMIUM_SHOP': '/components/PremiumShop.tsx',
    'SCREEN_MARKETING_PANEL': '/components/marketing/MarketingPanelPage.tsx',
    'SCREEN_KUNDLI_MAKER': '/components/kundli/KundliMaker.tsx',
    'SCREEN_VIEW_PROFILE': '/components/ViewProfilePage.tsx',
    'SCREEN_MATCHES_BROWSE': '/components/MobileApp.tsx (Matches Tab)',
    'SCREEN_INTERESTS_CONNECTIONS': '/components/MobileApp.tsx (Interests Tab)',
    'SCREEN_CHAT_LIST': '/components/MobileApp.tsx (Chat Tab)',
    'SCREEN_CHAT_CONVERSATION': '/components/ChatInterface.tsx or MobileApp.tsx',
    'SCREEN_SETTINGS': '/components/MobileApp.tsx (Settings Tab)',
  };

  return fileMap[screenName] || '/components/[ComponentName].tsx';
};

export default DebugScreenInfo;